import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  DownloadCloud,
  Loader2,
  AlertCircle,
  ClipboardCopy,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { importClassesFromExcel } from "../../../api/elearning/classes/classes";
import { showErrorToast, showSuccessToast, showWarningToast } from "../../../components/ConfirmDialog";
import { startClassImportModalTour } from "./classImportModalTour";

const downloadClassImportTemplate = () => {
  const rows = [
    [
      "Tên lớp học (tuỳ chọn - để trống = tự sinh: MãKhóaYY + mã ngẫu nhiên, giống form tạo lớp)",
      "Chi nhánh (UUID hoặc tên)",
      "Loại lớp",
      "Khóa học (code)",
      "Đối tác trường (UUID hoặc tên - bắt buộc nếu loại trường học)",
      "Giáo viên (email)",
      "Ngày bắt đầu",
      "Ngày kết thúc",
      "Phòng học",
      "Thời gian học (lịch chuẩn)",
    ],
    [
      "",
      "Chi nhánh Hà Nội",
      "trung tâm",
      "TDA2024",
      "",
      "giaovien1@ima.vn",
      "2025-06-01",
      "2025-08-01",
      "Phòng A1",
      "T2,T4,T6|18:00-20:00",
    ],
    [
      "Lớp Kỹ năng tại trường",
      "Chi nhánh Hà Nội",
      "trường học",
      "KN2024",
      "Trường THCS ABC",
      "giaovien2@ima.vn",
      "2025-07-05",
      "2025-09-05",
      "",
      "T3,T5|14:00-16:00",
    ],
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Danh sách lớp");
  XLSX.writeFile(wb, "mau_nhap_lop_hoc.xlsx");
};

const COLUMN_HELP = [
  "Tên lớp học — tuỳ chọn. Để trống thì hệ thống tự sinh giống form tạo lớp: {Mã khóa học}{2 số năm}{6 ký tự ngẫu nhiên} (ví dụ TDA2025A1B2C3); nếu khóa không có mã thì dùng tiền tố LOP.",
  "Chi nhánh (UUID hoặc tên đúng trên hệ thống) — bắt buộc. Nếu trùng tên chi nhánh, dùng UUID.",
  "Loại lớp — trung tâm / trường học (hoặc center / school). Để trống = trung tâm.",
  "Khóa học (code) — mã khóa học đã có trên hệ thống.",
  "Đối tác trường — bắt buộc khi loại là trường học: UUID hoặc tên CRM (ưu tiên theo chi nhánh).",
  "Giáo viên (email) — tuỳ chọn, email nhân viên đã có trong HRM.",
  "Ngày bắt đầu / Ngày kết thúc — định dạng YYYY-MM-DD.",
  "Phòng học — tuỳ chọn.",
  "Thời gian học — lịch chuẩn, ví dụ T2,T4,T6|18:00-20:00 (để trống nếu chưa có).",
  "Trùng lớp: hệ thống chỉ chặn khi trùng Tên lớp + Trường (hoặc Chi nhánh nếu lớp trung tâm) + Ngày bắt đầu — có thể nhiều lớp cùng tên \"Lớp 1.1\" ở trường/năm khác nhau.",
];

const ClassImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLastResult(null);
    }
  }, [isOpen]);

  const reset = () => {
    setFile(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    setLastResult(null);
    onClose?.();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const name = selected.name.toLowerCase();
    const ok = name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
    if (!ok) {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
      return;
    }
    setFile(selected);
    setLastResult(null);
  };

  const copyErrors = async () => {
    const lines = lastResult?.errors;
    if (!lines?.length) return;
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      showSuccessToast("Đã sao chép danh sách lỗi.");
    } catch {
      showErrorToast("Không thể sao chép. Thử chọn và copy thủ công.");
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      showErrorToast("Vui lòng chọn file để nhập");
      return;
    }
    setLoading(true);
    setLastResult(null);
    try {
      const data = await importClassesFromExcel(file);
      const imported = Number.isFinite(data?.imported) ? data.imported : 0;
      const rowErrors = Array.isArray(data?.errors) ? data.errors : [];

      if (imported > 0 || rowErrors.length === 0) {
        onSuccess?.();
      }

      if (rowErrors.length > 0) {
        setLastResult({
          imported,
          errors: rowErrors,
          message: typeof data?.message === "string" ? data.message : "",
        });
        showWarningToast(
          data?.message ||
            (imported > 0
              ? `Đã nhập ${imported} lớp; còn ${rowErrors.length} dòng lỗi — xem bên dưới.`
              : `Không nhập được lớp nào — ${rowErrors.length} lỗi — xem bên dưới.`)
        );
        setFile(null);
        return;
      }

      const msg =
        typeof data?.message === "string"
          ? data.message
          : data?.imported != null
            ? `Đã nhập ${data.imported} lớp học.`
            : "Nhập lớp học từ file thành công.";
      showSuccessToast(msg);
      reset();
      setLastResult(null);
      onClose?.();
    } catch (err) {
      const apiMsg = err.response?.data?.message || err.response?.data?.error;
      showErrorToast(apiMsg || "Không thể nhập file. Kiểm tra định dạng và dữ liệu.");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickTour = () => {
    window.setTimeout(() => startClassImportModalTour(), 80);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            className="relative w-full max-w-lg max-h-[min(92vh,720px)] flex flex-col bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden text-left"
            data-tour="class-import-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50/80 shrink-0"
              data-tour="class-import-header"
            >
              <div>
                <h2 className="text-xl font-black text-gray-800 flex items-center gap-2 uppercase tracking-tight">
                  <UploadCloud className="text-red-600 shrink-0" size={24} />
                  Nhập lớp từ Excel
                </h2>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  Dùng đúng file mẫu (cột Chi nhánh, Loại lớp, Đối tác trường khi cần).
                </p>
              </div>
              <div className="flex items-center gap-2" data-tour="class-import-guide-btn">
                <button
                  type="button"
                  onClick={handleQuickTour}
                  title="Hướng dẫn nhanh"
                  aria-label="Hướng dẫn nhanh"
                  className="group relative inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
                >
                  <Sparkles className="h-4.5 w-4.5" strokeWidth={2.2} />
                  <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
                    Hướng dẫn nhanh
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-200 text-gray-400 transition-all disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
              <div
                className="border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:border-red-300 hover:bg-red-50/20 transition-all relative text-center"
                data-tour="class-import-upload"
              >
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <div className="w-14 h-14 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <FileSpreadsheet size={28} />
                </div>
                <p className="text-sm font-bold text-gray-800">
                  {file ? file.name : "Chọn hoặc kéo thả file vào đây"}
                </p>
                <p className="text-[11px] text-gray-400 mt-1 font-medium">.xlsx, .xls hoặc .csv</p>
              </div>

              <button
                type="button"
                onClick={downloadClassImportTemplate}
                data-tour="class-import-template"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 bg-red-50/50 text-red-700 text-xs font-bold uppercase tracking-wide hover:bg-red-50 transition-all"
              >
                <DownloadCloud size={16} />
                Tải file mẫu Excel (.xlsx)
              </button>
              <p className="text-[10px] text-gray-500 text-center leading-snug">
                File mẫu được xuất trực tiếp dạng Excel (.xlsx) để mở đúng cột và giữ nguyên tiếng Việt.
              </p>

              <div
                className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4"
                data-tour="class-import-columns"
              >
                <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">
                  Cột trong mẫu (dòng 1 = tiêu đề)
                </p>
                <ul className="text-[11px] text-slate-600 leading-relaxed space-y-1.5 list-disc pl-4">
                  {COLUMN_HELP.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>

              <div
                className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl"
                data-tour="class-import-note"
              >
                <AlertCircle className="text-amber-600 shrink-0" size={18} />
                <p className="text-[11px] text-amber-900 leading-relaxed font-medium">
                  Dòng trống (không mã khóa và không dữ liệu khác) sẽ bị bỏ qua. Tên lớp có thể để trống
                  để tự sinh như khi tạo lớp thủ công. Trùng tên lớp trong cùng khóa học sẽ báo lỗi. Cột
                  lịch học phải đúng dạng chuẩn (ví dụ T2,T3|09:00-11:00).
                </p>
              </div>

              {lastResult?.errors?.length > 0 && (
                <div
                  className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4"
                  data-tour="class-import-errors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 text-rose-800">
                      <AlertCircle size={18} className="shrink-0" />
                      <span className="text-xs font-bold">
                        Kết quả: đã nhập {lastResult.imported} lớp — {lastResult.errors.length} lỗi
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={copyErrors}
                      className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-rose-700 hover:text-rose-900 px-2 py-1 rounded-lg border border-rose-200 bg-white/80"
                    >
                      <ClipboardCopy size={14} />
                      Sao chép lỗi
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded-xl bg-white/90 border border-rose-100/80 p-3 text-[11px] font-mono text-rose-900 whitespace-pre-wrap break-words">
                    {lastResult.errors.slice(0, 80).join("\n")}
                    {lastResult.errors.length > 80 ? (
                      <span className="block mt-2 text-rose-600 font-sans font-medium">
                        … và {lastResult.errors.length - 80} lỗi nữa (bấm Sao chép lỗi để xem đủ).
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-rose-700/90 mt-2 flex items-center gap-1">
                    <CheckCircle2 size={12} className="shrink-0" />
                    Sửa file và tải lại; danh sách lớp đã nhập vẫn được giữ.
                  </p>
                </div>
              )}
            </div>

            <div
              className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0"
              data-tour="class-import-actions"
            >
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                Đóng
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !file}
                className="px-6 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-100 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                {loading ? "Đang xử lý…" : "Nhập dữ liệu"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ClassImportModal;
